import type { CatalogueStockConfirmation } from "@/lib/catalogue-stock-confirmation";
// Shared types and constants for the Product Detail feature.
// Kept here so the orchestrating page and the per-tab feature modules share a
// single source of truth instead of re-declaring row shapes and UI tokens.

export type ActiveTab =
  | "overview"
  | "bundle"
  | "variants"
  | "media"
  | "inventory"
  | "suppliers"
  | "channels"
  | "storefront"
  | "advanced"
  | "history";

export interface ProductRow {
  id: string;
  name: string;
  reference: string | null;
  sku: string | null;
  barcode: string | null;
  category_id: string | null;
  description: string | null;
  short_description: string | null;
  seo_title: string | null;
  meta_description: string | null;
  seo_keywords?: string | null;
  url_slug?: string | null;
  og_title?: string | null;
  og_description?: string | null;
  og_image_url?: string | null;
  storefront_visible?: boolean | null;
  is_featured?: boolean | null;
  min_purchase_qty?: number | null;
  max_purchase_qty?: number | null;
  tags: string[] | null;
  brand: string | null;
  product_type: string | null;
  status: string;
  unit: string;
  cost_price: number | null;
  selling_price: number | null;
  reorder_point: number;
  reorder_qty: number;
  weight_kg: number | null;
  length_cm: number | null;
  width_cm: number | null;
  height_cm: number | null;
  manufacturer: string | null;
  model_number: string | null;
  mpn: string | null;
  country_of_origin: string | null;
  hs_tariff_code: string | null;
  customs_description: string | null;
  unit_of_measure: string | null;
  tax_profile: string | null;
  shipping_profile: string | null;
  condition: string | null;
  identifiers_not_applicable?: boolean | null;
  categories?: { name: string } | null;
}

export interface BalanceRow {
  id: string;
  variant_id: string | null;
  location_id: string | null;
  on_hand: number;
  allocated: number;
  available: number;
  incoming: number;
  transferring: number;
  locations?: { name: string } | null;
  variants?: { sku: string | null; option_values: Record<string, string> | null } | null;
}

export interface VariantRow {
  id: string;
  sku: string | null;
  barcode: string | null;
  option_values: Record<string, string> | null;
  cost_price: number | null;
  selling_price: number | null;
  reorder_point: number | null;
  reorder_qty: number | null;
  weight_kg: number | null;
  length_cm: number | null;
  width_cm: number | null;
  height_cm: number | null;
  status: string | null;
}

export interface LedgerRow {
  id: string;
  entry_type: string;
  qty_change: number;
  created_at: string;
  reason: string | null;
  reference_type: string | null;
  reference_id: string | null;
  locations?: { name: string } | null;
}

export interface ExternalIdRow {
  id: string;
  connector_id: string | null;
  external_type: string;
  external_id: string;
  sync_status: string;
  created_at: string;
}

export interface SupplierLinkRow {
  id: string;
  supplier_id: string;
  variant_id: string | null;
  supplier_sku: string | null;
  supplier_price: number | null;
  min_order_qty: number | null;
  order_multiple: number | null;
  lead_time_days: number | null;
  is_preferred: boolean;
  suppliers?: { name: string } | null;
  variants?: { sku: string | null } | null;
}

export interface ArchiveReadiness {
  can_archive: boolean;
  blockers: { code: string; message: string; count: number }[];
  warnings: { code: string; message: string; count: number }[];
}

export interface DeleteSafety {
  stock_confirmation?: CatalogueStockConfirmation;
  can_delete: boolean;
  blockers: { code: string; message: string; count: number }[];
  warnings: { code: string; message: string; count: number }[];
  requires_stock_disposition: boolean;
  on_hand: number;
  historical_sales: number;
  historical_movements: number;
}

export interface QualityResult {
  status: string;
  blocking: number;
  warnings: number;
  checks: { level: string; code: string; message: string }[];
}

export const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
export const labelCls = "block text-xs font-medium text-slate-500 mb-1";

export const CHECK_TARGET: Record<string, { tab: "overview" | "variants" | "media" | "inventory" | "suppliers" | "advanced"; label: string }> = {
  no_active_variant: { tab: "variants", label: "Variants" },
  missing_description: { tab: "overview", label: "Edit" },
  missing_category: { tab: "overview", label: "Edit" },
  missing_brand: { tab: "overview", label: "Edit" },
  missing_media: { tab: "media", label: "Media" },
  missing_variant_sku: { tab: "variants", label: "Variants" },
  missing_variant_price: { tab: "variants", label: "Variants" },
  missing_variant_selling_price: { tab: "variants", label: "Variants" },
  missing_variant_cost: { tab: "variants", label: "Variants" },
  missing_dimensions: { tab: "variants", label: "Variants" },
  missing_variant_weight: { tab: "variants", label: "Variants" },
  missing_variant_dimensions: { tab: "variants", label: "Variants" },
  missing_gtin: { tab: "advanced", label: "Product Data" },
  channel_missing_gtin: { tab: "advanced", label: "Product Data" },
  out_of_stock: { tab: "inventory", label: "Stock" },
  low_stock: { tab: "inventory", label: "Stock" },
  missing_supplier: { tab: "suppliers", label: "Suppliers" },
};
